import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppStorageService } from '@app/services/app-storage.service';
import { environment } from '@env/environment';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';

const STORAGE_NAME: string = environment.localStorageName + '_language';

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'it', 'fi'] as const;
type TSupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LANGUAGE_NAMES: Record<TSupportedLanguage, string> = {
    en: 'English',
    de: 'Deutsch',
    fr: 'Français',
    it: 'Italiano',
    fi: 'Suomi',
};

@Injectable({
    providedIn: 'root',
})
export class AppLanguageService {
    private appStorage: AppStorageService = inject(AppStorageService);
    private translate: TranslateService = inject(TranslateService);

    readonly supportedLanguages = SUPPORTED_LANGUAGES;
    readonly languageNames = LANGUAGE_NAMES;

    current: Signal<string> = toSignal(
        (this.translate.onLangChange as Observable<LangChangeEvent>).pipe(
            map((lang: LangChangeEvent) => lang.lang),
        ),
        { initialValue: 'en' },
    );

    fnInit() {
        const stored: string | undefined = this.appStorage.getItem(STORAGE_NAME);
        const resolved = this.resolve(stored);
        if (resolved !== this.translate.currentLang) {
            this.translate.use(resolved);
        }
    }

    set(lang: string) {
        this.translate.use(lang);
        this.appStorage.setItem(STORAGE_NAME, lang);
    }

    private resolve(stored: string | undefined): string {
        const supported = SUPPORTED_LANGUAGES as readonly string[];
        if (stored && supported.includes(stored)) return stored;
        const browser = navigator.language.split('-')[0];
        if (supported.includes(browser)) return browser;
        return 'en';
    }
}
